import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { Localization } from "@viamap/viamap2-common";
import { ConflictProfile, WFSEndpointsType } from "./ConflictSearchDialog";
import { KeyValueLine, KeyValuesHorizontal, ViewButtonCheck, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { useContext, useEffect, useState } from "react";
import { actionSetLayerHierarchy, MapitStateActionType, MapitStateContext } from "src/states/MapitState";
import { addConflictDisplayLayer, removeDisplayLayer } from "src/utils/mapLayerHelpers";
import { GeoJSONSource } from "maplibre-gl";
import { handleClickProfileSearch } from "./ConflictSearchCalculations";
import { Spinner } from "react-bootstrap";
import { BsArrowUpRightSquareFill, BsPrinter } from "react-icons/bs";
import { Persistence } from "src/managers/Persistence";
import { LayerHierarchyEndNode, LayerInfo } from "src/common/managers/Types";
import { OldHierarchy } from "src/managers/OldHierarchy";

export type ConflictSearchParams = ConflictProfile<WFSEndpointsType> & { geom, buffer?: number };
export type Conflict = WFSEndpointsType & { conflict: any, intersect: any, progress: number, err: string, matchedCount: number };

export default function ConflictSearchResults({ window }: { window: ManagedWindow }) {
   const searchParams: ConflictSearchParams = window.windowParams?.searchParams || {};
   const [results, setResults] = useState<Conflict[]>([]);
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState, dispatch: mapitDispatch } = useContext(MapitStateContext);
   const [currentConflict, setCurrentConflict] = useState<Conflict | null>(null);
   const [onlyConflicts, setOnlyConflicts] = useState(false);

   const filteredResults = onlyConflicts ? results.filter(r => r.matchedCount || r.err) : results;
   const isPages = !!searchParams.pages;
   const sourceLayers = isPages ? Object.groupBy(filteredResults, r => window.windowParams?.searchParams?.pages.find((p) => p.layers.some(l => l.source === r.source && l.layer === r.layer))?.name || "Unknown") : Object.groupBy(filteredResults, r => r.source);

   const totalConflicts = results.reduce((acc, r) => acc + (Math.max(0, r.matchedCount)), 0)

   useEffect(() => {
      let x = mapitState.map?.getMapPop()
      if (x) {
         addConflictDisplayLayer(x, "conflict-search-results-V2");
         return () => {
            removeDisplayLayer(x, "conflict-search-results-V2");
         }
      }
   }, [mapitState.map, searchParams])

   useEffect(() => {
      if (window.windowParams?.searchParams) {
         handleClickProfileSearch(window.windowParams.searchParams, setResults);
         return () => {
            setResults([]);
         }
      }
   }, [searchParams])

   return (
      <AdvancedDragModal
         PosDefault={{ left: "100%", top: "0px", width: "400px", transform: "translate(-100%, 0)" }}
         title={Localization.getText("Conflict search results")}
         onClose={() => windowDispatch(closeWindow(window.id))}
         subHeaderContent={<>
            <ViewButtonCheck checked={onlyConflicts} onCheck={function (e: any, a: any): void {
               setOnlyConflicts(!a);
            }}>{Localization.getText("Only conflicts")}</ViewButtonCheck>
            <ViewButtonIcon
               disabled={!totalConflicts}
               onClick={async () => {
                  let previouslyLayerHiearchy = structuredClone(mapitState.layerHierarchy);
                  let lowestID = OldHierarchy.extractLowestId(mapitState.layerHierarchy);
                  let lis = await Promise.all(await prepareForPlot(results).then(layers => {
                     return layers.map((layer) => {
                        return new Promise((resolve) => {
                           mapitDispatch({
                              type: MapitStateActionType.AddDataLayer,
                              payload: { layerInfo: layer },
                              callbacks: {
                                 onLayerAdded: (li) => {
                                    resolve(li);
                                 }
                              }
                           })
                        })
                     }) as Promise<LayerInfo>[];
                  }));
                  previouslyLayerHiearchy.children.push(
                     {
                        id: lowestID - 1, isCollapsed: true, name: Localization.getText("Conflict search"), children: lis.map((li: LayerInfo) => {
                           let endNode: LayerHierarchyEndNode = {
                              id: li.layerId
                           }
                           return endNode;
                        })
                     }
                  );
                  mapitDispatch(actionSetLayerHierarchy(previouslyLayerHiearchy));
               }}
               title={Localization.getText("Add as layer")}
            > <BsArrowUpRightSquareFill
               /></ViewButtonIcon>
            <ViewButtonIcon
               disabled={!totalConflicts}
               title={Localization.getText("Plot")}
               onClick={(() => {
                  if (results.length) {
                     prepareForPlot(results).then(layers => {
                        windowDispatch(openWindow(WindowId.PlotScreen, { externalSettings: { dataLayer: layers.map((a) => ({ ...a, visible: true })) } }))
                     });
                  }
               })}><BsPrinter /></ViewButtonIcon>
         </>}
      >
         <ADModalBody >
            {/* <div className="ConflictSummary" > */}
            <KeyValuesHorizontal>
               <KeyValueLine valKey={Localization.getText("Total searches")} value={results.reduce((acc, r) => acc + r.progress, 0)} />
               <KeyValueLine valKey={Localization.getText("Conflicts found")} value={results.reduce((acc, r) => acc + (Math.max(0, r.matchedCount)), 0)} />
               <KeyValueLine valKey={Localization.getText("Searches failed")} value={results.reduce((acc, r) => acc + (r.err ? 1 : 0), 0)} />
               <KeyValueLine valKey={Localization.getText("Sources")} value={Object.keys(sourceLayers).length} />
            </KeyValuesHorizontal>
            {/* </div> */}
            <div className="ConflictSearchScrollContainer">
               {
                  Object.entries(sourceLayers).sort((a, b) => a[0].localeCompare(b[0])).map(([source, conflicts]) => (
                     conflicts ? <ConflictSourceGroup key={source} source={source} conflicts={conflicts} selectedConflict={currentConflict ?? undefined}

                        onLayerClick={(conflict) => {
                           let map = mapitState.map?.getMapPop();
                           if (map && conflict.conflict) {
                              let source = map.getSource("conflict-search-results-V2") as GeoJSONSource;
                              if (source) {
                                 source.setData(conflict.conflict);
                                 setCurrentConflict(conflict);
                              }
                           }
                        }}
                     /> : null
                  ))
               }
            </div>
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function ConflictSourceGroup({ source, conflicts, onLayerClick, selectedConflict }: { source: string, conflicts: Conflict[], onLayerClick?: (geom) => void, selectedConflict?: Conflict }) {
   const { state: mapitState } = useContext(MapitStateContext);

   return (
      <div className="ConflictSourceGroup">
         <div className="ConflictSourceGroupHeader" >
            <div className="ConflictSourceGroupTitle"><b>{source}</b> </div>
            <span>{Localization.getText("Conflicts")}</span>
         </div>
         <div className="ConflictSourceGroupContent">
            {conflicts.map((conflict, i) => (
               <div key={i} className="ConflictItem" onClick={() => {
                  let map = mapitState.map?.getMapPop();
                  if (map && conflict.conflict) {
                     onLayerClick?.(conflict);
                  }
               }}>
                  <div className={selectedConflict === conflict ? "ConflictItemSelected" : ""}>{conflict.title ?? conflict.layer}</div>
                  {
                     conflict.err ? <div className="ConflictItemError">{Localization.getText("Error")}</div> :
                        <div className="ConflictItemDetails">{!conflict.progress ? <Spinner animation="border" size="sm" /> : conflict.matchedCount}</div>
                  }
               </div>
            ))}
         </div>
      </div>
   )
}

async function prepareForPlot(conflicts: Conflict[]) {
   let results = await Promise.allSettled(conflicts.map(async (c) => {
      if (c.conflict && c.conflict.features) {
         return await new Promise((resolve, reject) => {
            Persistence.createLayersFromGeoJSON(c.title ?? c.layer, c.conflict, "", (li) => {
               resolve(li);
            });
         })
      } throw new Error("No conflict data");
   }));
   return results.filter(r => r.status === "fulfilled").map(r => (r as PromiseFulfilledResult<any>).value) as LayerInfo[];
}